/**
 * Which piece of a symbol's art a renderer draws, from its art manifest entry
 * (board: symbol_id → entry; scene: symbol_id → role → entry): the catalog's
 * (TT-1310), each entry the symbol's default piece in its role, listing every
 * active piece in `pieces`, each tagged with its `styles`.
 *
 * The symbol images' rule (symbolImageClient pickDoc): asked for styles, the
 * first piece tagged with any of them, case-insensitive; asked for none, or
 * with none matching, the default.
 */
export function pickArtPiece(entry, styles) {
  if (!entry || !styles?.length) return entry || null;
  const want = new Set(styles.map((s) => String(s).toLowerCase()));
  const tagged = (p) => Array.isArray(p.styles) && p.styles.some((s) => want.has(String(s).toLowerCase()));
  return entry.pieces.find(tagged) || entry;
}

/**
 * A symbol's scene art in `role` (backdrop | mid), from a scene manifest's
 * assets or the loaded map (scene/sceneAssets.js), both symbol_id → role → art;
 * null where it has none.
 */
export const sceneArt = (assets, symbolId, role) => assets?.[symbolId]?.[role] || null;

/** Requested styles as a stable key: hosts hand over a fresh array each render. */
export const stylesKey = (styles) =>
  styles?.length ? [...new Set(styles.map((s) => String(s).toLowerCase()))].sort().join(',') : '';

// A manifest's entries: the board's are its values (symbol_id → entry), the
// scene's one level down (symbol_id → role → entry).
const entriesOf = (assets) =>
  Object.values(assets || {}).flatMap((v) => (Array.isArray(v.pieces) ? [v] : Object.values(v)));

/** Every style a manifest's pieces are tagged with, sorted: what a host can ask for. */
export function manifestStyles(assets) {
  const out = new Set();
  for (const entry of entriesOf(assets))
    for (const p of entry.pieces) for (const s of p.styles || []) out.add(String(s).toLowerCase());
  return [...out].sort();
}
